import type { Expr, Stmt, ValueType } from '../ir.js';
import { elemOf, isArray, walkExpr, walkStmts, stmtExprs } from '../ir.js';
import { isCharLiteral } from '../types.js';
import { CodeWriter } from '../writer.js';
import type { Code, GenContext } from './context.js';
import { ATOM, joinParts, MISSING, numberLiteral, stringLiteral, wrap } from './context.js';

const P = { or: 1, and: 2, eq: 3, rel: 4, add: 5, mul: 6, unary: 7 };

const CPP_TYPE: Record<ValueType, string> = {
  int: 'int',
  double: 'double',
  string: 'string',
  char: 'char',
  bool: 'bool',
  // Lists are vectors: they are declared with the other variables and sized when the program
  // makes them, which is how a size read from the input can work at all.
  'int[]': 'vector<int>',
  'double[]': 'vector<double>',
  'string[]': 'vector<string>',
  'bool[]': 'vector<bool>',
  'int[][]': 'vector<vector<int>>',
  'double[][]': 'vector<vector<double>>',
  'string[][]': 'vector<vector<string>>',
  'bool[][]': 'vector<vector<bool>>',
};

const CPP_ZERO: Record<string, string> = {
  int: '0',
  double: '0',
  string: '""',
  bool: 'false',
};

/** A one-character literal as a C++ `char`: 'a', '\n', '\''. */
function charLiteral(c: string): string {
  const esc: Record<string, string> = { '\n': '\\n', '\t': '\\t', '\\': '\\\\', "'": "\\'" };
  return `'${esc[c] ?? c}'`;
}

/**
 * The C++ view, written the way Romanian classes write it: variables declared at the top of
 * `main` grouped by type, `cin >>` / `cout <<`, integer division with `/` on ints.
 */
export function generateCpp(ctx: GenContext): CodeWriter {
  const w = new CodeWriter('  ');
  const { program } = ctx;

  const expr = (e: Expr): Code => {
    switch (e.k) {
      case 'num':
        return { code: numberLiteral(e.value), prec: e.value < 0 ? P.unary : ATOM };
      case 'text':
        return { code: e.char ? charLiteral(e.value) : stringLiteral(e.value), prec: ATOM };
      case 'bool':
        return { code: String(e.value), prec: ATOM };
      case 'var':
        return { code: ctx.id(e.name), prec: ATOM };
      case 'read':
        // Only valid directly in `set … to`, which is handled as a statement below.
        return { code: MISSING, prec: ATOM };
      case 'arith': {
        const bothInt = ctx.type(e.left) === 'int' && ctx.type(e.right) === 'int';
        if (e.op === '/' && bothInt) {
          return {
            code: `(double) ${wrap(expr(e.left), P.unary)} / ${wrap(expr(e.right), P.mul + 1)}`,
            prec: P.mul,
          };
        }
        if (e.op === 'idiv' && !bothInt) {
          return {
            code: `(int) (${wrap(expr(e.left), P.mul)} / ${wrap(expr(e.right), P.mul + 1)})`,
            prec: P.unary,
          };
        }
        const op = e.op === 'idiv' ? '/' : e.op === 'mod' ? '%' : e.op;
        const prec = op === '+' || op === '-' ? P.add : P.mul;
        return {
          code: `${wrap(expr(e.left), prec)} ${op} ${wrap(expr(e.right), prec + 1)}`,
          prec,
        };
      }
      case 'compare': {
        const prec = e.op === '==' || e.op === '!=' ? P.eq : P.rel;
        const l = compareSide(e.left, e.right);
        const r = compareSide(e.right, e.left);
        return { code: `${wrap(l, P.add)} ${e.op} ${wrap(r, P.add)}`, prec };
      }
      case 'logic': {
        const prec = e.op === 'and' ? P.and : P.or;
        const op = e.op === 'and' ? '&&' : '||';
        return {
          // Nested and/or always get parentheses: clearer, and g++ -Wall asks for them.
          code: `${wrap(expr(e.left), P.and + 1)} ${op} ${wrap(expr(e.right), P.and + 1)}`,
          prec,
        };
      }
      case 'not':
        return { code: `!${wrap(expr(e.arg), P.unary)}`, prec: P.unary };
      case 'join': {
        // string + string: at least one side must be a std::string, so wrap a leading literal.
        const parts = joinParts(e).map((part, i) => {
          const t = ctx.type(part);
          if (t === 'string') {
            return part.k === 'text' && i === 0
              ? `string(${expr(part).code})`
              : wrap(expr(part), P.add + 1);
          }
          if (t === 'bool') return `(${wrap(expr(part), P.or + 1)} ? "true" : "false")`;
          // char + char would add their codes, so a leading character becomes a string.
          if (t === 'char') {
            return i === 0 ? `string(1, ${expr(part).code})` : wrap(expr(part), P.add + 1);
          }
          return `to_string(${expr(part).code})`;
        });
        return { code: parts.join(' + '), prec: P.add };
      }
      case 'length':
        // The cast keeps `i < (int) s.length()` free of the sign-compare warning, as for lists.
        return { code: `(int) ${asString(e.arg)}.length()`, prec: P.unary };
      case 'code':
        return { code: `(int) ${wrap(asChar(e.arg), P.unary)}`, prec: P.unary };
      case 'chr':
        return { code: `(char) ${wrap(expr(e.arg), P.unary)}`, prec: P.unary };
      case 'case':
        return {
          code: `(char) ${e.upper ? 'toupper' : 'tolower'}(${asChar(e.arg).code})`,
          prec: P.unary,
        };
      case 'find':
        // string::npos turned into an int is -1, which is what the blocks promise.
        return {
          code: `(int) ${asString(e.hay)}.find(${asChar(e.needle).code})`,
          prec: P.unary,
        };
      case 'substr':
        return {
          code: `${asString(e.arg)}.substr(${expr(e.from).code}, ${expr(e.len).code})`,
          prec: ATOM,
        };
      case 'at': {
        const idx = `[${expr(e.index).code}]${e.index2 ? `[${expr(e.index2).code}]` : ''}`;
        return { code: `${ctx.id(e.name)}${idx}`, prec: ATOM };
      }
      case 'size':
        // The cast keeps `i <= a.size()` free of the sign-compare warning g++ -Wall raises.
        return {
          code:
            e.dim === 2 ? `(int) ${ctx.id(e.name)}[0].size()` : `(int) ${ctx.id(e.name)}.size()`,
          prec: ATOM,
        };
      case 'missing':
        return { code: MISSING, prec: ATOM };
    }
  };

  const e = (x: Expr) => expr(x).code;

  /** Where C++ wants a `char`, a one-letter text is written 'a' rather than "a". */
  const asChar = (x: Expr): Code =>
    isCharLiteral(x) && x.k === 'text' ? { code: charLiteral(x.value), prec: ATOM } : expr(x);

  /** Something `.find` / `.substr` can be called on: a literal needs to become a string first. */
  const asString = (x: Expr): string =>
    x.k === 'text' ? `string(${expr(x).code})` : wrap(expr(x), ATOM);

  /** One side of a comparison: C++ compares a char only with a char, and text only with text. */
  const compareSide = (x: Expr, other: Expr): Code => {
    const t = ctx.type(x);
    const o = ctx.type(other);
    if (o === 'char' && isCharLiteral(x)) return asChar(x);
    if (t === 'char' && o === 'string' && !isCharLiteral(other)) {
      return { code: `string(1, ${expr(x).code})`, prec: ATOM };
    }
    return expr(x);
  };

  /** One `<<` operand; bools print as true/false to match the other views. */
  const coutPart = (x: Expr): string =>
    ctx.type(x) === 'bool'
      ? `(${wrap(expr(x), P.or + 1)} ? "true" : "false")`
      : wrap(expr(x), P.add);

  const block = (body: readonly Stmt[]) => w.indented(() => body.forEach(stmt));

  const stmt = (s: Stmt): void => {
    switch (s.k) {
      case 'set': {
        const name = ctx.id(s.name);
        if (s.value.k === 'read') {
          const line = s.value.as === 'text' ? `getline(cin >> ws, ${name});` : `cin >> ${name};`;
          w.stmt(s, () => w.line(line));
        } else {
          w.stmt(s, () => w.line(`${name} = ${e(s.value)};`));
        }
        break;
      }
      case 'change': {
        const name = ctx.id(s.name);
        const d = s.delta;
        const text =
          d.k === 'num' && d.value === 1
            ? `${name}++;`
            : d.k === 'num' && d.value === -1
              ? `${name}--;`
              : d.k === 'num' && d.value < 0
                ? `${name} -= ${numberLiteral(-d.value)};`
                : `${name} += ${e(d)};`;
        w.stmt(s, () => w.line(text));
        break;
      }
      case 'print': {
        const parts = (s.value.k === 'join' ? joinParts(s.value) : [s.value]).map(coutPart);
        if (s.end === 'newline') parts.push('endl');
        if (s.end === 'space') parts.push('" "');
        w.stmt(s, () => w.line(`cout << ${parts.join(' << ')};`));
        break;
      }
      case 'if':
        w.stmt(
          s,
          () => w.line(`if (${e(s.branches[0]!.cond)}) {`),
          () => {
            s.branches.forEach((b, i) => {
              if (i > 0) w.mapExpr(b.cond, w.line(`} else if (${e(b.cond)}) {`));
              block(b.body);
            });
            if (s.orElse) {
              w.line('} else {');
              block(s.orElse);
            }
            w.line('}');
          },
        );
        break;
      case 'repeat': {
        const k = ctx.counter(s.id);
        w.stmt(
          s,
          () => w.line(`for (int ${k} = 1; ${k} <= ${wrap(expr(s.times), P.add)}; ${k}++) {`),
          () => {
            block(s.body);
            w.line('}');
          },
        );
        break;
      }
      case 'while':
        w.stmt(
          s,
          () => w.line(`while (${e(s.cond)}) {`),
          () => {
            block(s.body);
            w.line('}');
          },
        );
        break;
      case 'for': {
        const i = ctx.id(s.name);
        const [cmp, step] = s.down ? ['>=', '--'] : ['<=', '++'];
        w.stmt(
          s,
          () =>
            w.line(
              `for (${i} = ${e(s.from)}; ${i} ${cmp} ${wrap(expr(s.to), P.add)}; ${i}${step}) {`,
            ),
          () => {
            block(s.body);
            w.line('}');
          },
        );
        break;
      }
      case 'newArray': {
        const name = ctx.id(s.name);
        const zero = CPP_ZERO[s.elem] ?? '0';
        // A list the student numbers from 1 gets one extra place, so place n really exists.
        const size = (d: Expr) => (s.base === 1 ? `${wrap(expr(d), P.add)} + 1` : e(d));
        const [rows, cols] = s.dims;
        const text = cols
          ? `${name}.assign(${size(rows)}, vector<${CPP_TYPE[s.elem]}>(${size(cols)}, ${zero}));`
          : `${name}.assign(${size(rows)}, ${zero});`;
        w.stmt(s, () => w.line(text));
        break;
      }
      case 'setAt': {
        const place = `${ctx.id(s.name)}[${e(s.index)}]${s.index2 ? `[${e(s.index2)}]` : ''}`;
        const line =
          s.value.k === 'read'
            ? s.value.as === 'text'
              ? `getline(cin >> ws, ${place});`
              : `cin >> ${place};`
            : `${place} = ${e(s.value)};`;
        w.stmt(s, () => w.line(line));
        break;
      }
      case 'break':
        w.stmt(s, () => w.line('break;'));
        break;
    }
  };

  const usesString =
    Object.values(program.types).includes('string') ||
    [...walkStmts(program.body)].some((st) =>
      stmtExprs(st).some((x) =>
        [...walkExpr(x)].some((n) => ['join', 'length', 'find', 'substr'].includes(n.k)),
      ),
    );

  const usesCase = [...walkStmts(program.body)].some((st) =>
    stmtExprs(st).some((x) => [...walkExpr(x)].some((n) => n.k === 'case')),
  );

  const usesVector = Object.values(program.types).some(isArray);

  w.line('#include <iostream>');
  if (
    usesString ||
    Object.values(program.types).some((t) => elemOf(t) === 'string' && isArray(t))
  ) {
    w.line('#include <string>');
  }
  if (usesVector) w.line('#include <vector>');
  if (usesCase) w.line('#include <cctype>');
  w.line('using namespace std;');
  w.line('');
  w.line('int main() {');
  w.indented(() => {
    const byType = new Map<ValueType, string[]>();
    for (const v of program.variables) {
      const t = program.types[v] ?? 'int';
      byType.set(t, [...(byType.get(t) ?? []), ctx.id(v)]);
    }
    for (const [t, names] of byType) w.line(`${CPP_TYPE[t]} ${names.join(', ')};`);
    program.body.forEach(stmt);
    w.line('return 0;');
  });
  w.line('}');
  w.mapAll(program.startId);
  return w;
}
